import React from 'react';
import { Card } from '@rezi/ui/card';
import { Input } from '@rezi/ui/input';
import ReactMarkdown from 'react-markdown';

const SEARCH_ENDPOINT =
  'https://docs-search-service-924097261936.us-central1.run.app/search-docs';

const DocsSearch = () => {
  const [query, setQuery] = React.useState('');
  const [response, setResponse] = React.useState<string | null>(null);
  const [warnings, setWarnings] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = query.trim();

    if (!trimmed) {
      setError('Enter a question to search the docs.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResponse(null);
    setWarnings(null);

    try {
      const res = await fetch(SEARCH_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query: trimmed }),
      });

      if (!res.ok) {
        throw new Error(`Search request failed (${res.status}).`);
      }

      const data = await res.json();
      const structuredResponse = data?.structured_response;
      const answer = structuredResponse?.response;

      if (!answer) {
        throw new Error('No response returned from the docs search.');
      }

      setResponse(answer);
      setWarnings(structuredResponse?.warnings ?? null);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Something went wrong while searching the docs.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-10 mb-30 flex flex-col gap-4">
      <form onSubmit={handleSubmit}>
        <div className="mt-3 flex flex-col items-center justify-center gap-3 align-middle">
          <label className="text-2xl font-bold" htmlFor="docs-search-input">
            Ask a question
          </label>
          <div className="w-full max-w-[600px]">
            <Input
              id="docs-search-input"
              name="docs-search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder='e.g. "what stack does app use?"'
              inputSize="large"
              fullWidth
              className="rounded-full!"
              loading={isLoading}
            />
          </div>
        </div>
        {error && <div className="mt-3 text-xs text-red-600">{error}</div>}
      </form>

      {response && (
        <Card heading="Answer" collapse={true} defaultExpanded={true}>
          <div className="relative my-4 flex flex-col gap-3">
            {warnings && (
              <div className="rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-900">
                <div className="prose prose-sm">
                  <ReactMarkdown>{warnings}</ReactMarkdown>
                </div>
              </div>
            )}

            <div className="prose prose max-w-none text-gray-700">
              <ReactMarkdown>{response}</ReactMarkdown>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default DocsSearch;
